import { useEffect, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["In Process", "Completed", "Cancelled", "Lost"];
const COLOR = (s) => ({ "In Process": "bg-blue-100 text-blue-700", Completed: "bg-emerald-100 text-emerald-700", Cancelled: "bg-slate-100 text-slate-500", Lost: "bg-red-100 text-red-700" }[s]);
const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(n || 0)}`;

export default function AdminDeals() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [completeFor, setCompleteFor] = useState(null);

  const load = () => {
    setLoading(true);
    const params = status !== "all" ? { status } : {};
    api.get("/admin/deals", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Deals</h1>
        <div className="w-44">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="deal-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-5 space-y-3">
          {items.length === 0 && <p className="py-12 text-center text-slate-500">No deals yet. Create a deal from a Lead.</p>}
          {items.map((d) => (
            <div key={d.id} data-testid={`deal-row-${d.deal_code}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-slate-400">{d.deal_code}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${COLOR(d.status)}`}>{d.status}</span>
                  </div>
                  <p className="mt-0.5 font-semibold text-slate-900">{d.machine_name} <span className="font-mono text-xs text-slate-400">{d.machine_code}</span></p>
                  <p className="text-sm text-slate-500">Buyer: {d.buyer_name} • Seller: {d.seller_name}</p>
                  <p className="text-sm font-semibold text-slate-800">Sale price: {inr(d.final_sale_price)}</p>
                  {d.service_charge && (
                    <p className="mt-1 text-xs text-slate-500">Service charge <span className="font-mono">{d.service_charge.sc_code}</span> · {inr(d.service_charge.amount)} · <span className="font-bold">{d.service_charge.payment_status}</span></p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {d.status === "In Process" ? (
                    <button data-testid={`complete-deal-${d.deal_code}`} onClick={() => setCompleteFor(d)} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white">
                      <CheckCircle2 size={15} /> Mark Sold / Complete
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">{d.actual_closing_date ? `Closed ${String(d.actual_closing_date).slice(0, 10)}` : ""}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {completeFor && <CompleteModal deal={completeFor} onClose={() => setCompleteFor(null)} onSaved={() => { setCompleteFor(null); load(); }} />}
    </div>
  );
}

function CompleteModal({ deal, onClose, onSaved }) {
  const [price, setPrice] = useState(deal.final_sale_price || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/deals/${deal.id}/complete`, { final_sale_price: Number(price) || 0 });
      toast.success(`Deal completed. Service charge ${data.service_charge.sc_code} (${inr(data.service_charge.amount)}) generated.`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="deal-complete-modal" className="max-w-md">
        <DialogHeader><DialogTitle>Complete Deal — {deal.machine_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">This marks the machine <strong>SOLD</strong>, removes it from active matching, and generates the fixed post-sale service charge.</p>
        <div className="mt-3"><Label>Final sale price (₹)</Label><Input data-testid="complete-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
        <button data-testid="complete-confirm" onClick={save} disabled={saving} className="mt-4 w-full rounded-full bg-emerald-600 py-2.5 font-bold text-white disabled:opacity-60">{saving ? "Completing…" : "Confirm & Complete"}</button>
      </DialogContent>
    </Dialog>
  );
}
